// Sample data for the Custom Pitch Strategy Builder illustration (FUTURE VISION).
// This is a planned capability shown as a visual demonstration only. Nothing here saves, runs or activates a strategy.

export type NodeKind = 'start' | 'conversation' | 'decision' | 'action' | 'end'

export type ToneDimension = 'Energy' | 'Formality' | 'Persuasiveness' | 'Warmth' | 'Detail'

export interface StrategyNode {
    id: string
    label: string
    kind: NodeKind
    x: number
    y: number
    purpose: string
    behaviour: string
    responses: string[]
    asset: string | null
    next: string
    tonePreset: string
    tone: Record<ToneDimension, number>
    test: {
        visitor: string
        bucky: string
        outcome: string
    }
}

export interface StrategyEdge {
    from: string
    to: string
    label?: string
}

export interface StrategyAsset {
    id: string
    number: string
    title: string
    type: string
    icon: 'image' | 'info' | 'pdf' | 'promo' | 'event' | 'service'
}

export const strategyBuilder = {
    eyebrow: 'FUTURE VISION',
    title: 'Custom Pitch Strategy Builder',
    statement: 'Give businesses control over how Bucky promotes their products, services and experiences.',
    relation: 'Play-to-Pitch is how Bucky attracts and engages people. The Pitch Strategy Builder is the planned system that will let each business customise how Bucky pitches, what it shows and where it guides customers.',
    disclaimer: 'Planned capabilities, not working features.',
    sameStrategy: 'Guided and Visual are two views of the same pitching strategy. Every step card below is the same node on the canvas.',
    strategyName: 'Sample strategy',
    canvas: {
        width: 1000,
        height: 520,
        nodeWidth: 124,
        nodeHeight: 74
    }
} as const

const FRIENDLY_GUIDE = { Energy: 55, Formality: 35, Persuasiveness: 40, Warmth: 85, Detail: 55 }
const PROFESSIONAL = { Energy: 35, Formality: 85, Persuasiveness: 45, Warmth: 60, Detail: 75 }
const ENERGETIC = { Energy: 85, Formality: 35, Persuasiveness: 60, Warmth: 70, Detail: 45 }
const CALM = { Energy: 25, Formality: 65, Persuasiveness: 25, Warmth: 75, Detail: 65 }

export const strategyNodes: StrategyNode[] = [
    {
        id: 'start',
        label: 'Start customer session',
        kind: 'start',
        x: 65,
        y: 260,
        purpose: 'Begin a fresh session for each new visitor.',
        behaviour: 'Reset the conversation and load the business-approved information.',
        responses: ['Visitor starts the game', 'Visitor taps Bucky'],
        asset: null,
        next: 'Opening pitch',
        tonePreset: 'Friendly Guide',
        tone: FRIENDLY_GUIDE,
        test: {
            visitor: '(Taps to play)',
            bucky: 'Session started with a clean slate.',
            outcome: 'continue'
        }
    },
    {
        id: 'opening',
        label: 'Opening pitch',
        kind: 'conversation',
        x: 200,
        y: 260,
        purpose: 'Welcome the visitor and introduce the business in one sentence.',
        behaviour: 'Keep it short, friendly and relevant to the game moment.',
        responses: ['Listens', 'Asks something', 'Keeps playing'],
        asset: null,
        next: 'Customer interest checkpoint',
        tonePreset: 'Friendly Guide',
        tone: FRIENDLY_GUIDE,
        test: {
            visitor: 'Hi!',
            bucky: 'Hi! Great to see you. Can I show you what is new here?',
            outcome: 'continue'
        }
    },
    {
        id: 'checkpoint',
        label: 'Customer interest checkpoint',
        kind: 'decision',
        x: 345,
        y: 260,
        purpose: 'Understand what the visitor wants before pitching.',
        behaviour: 'Ask one question and route by the answer. Unclear answers get one polite clarification.',
        responses: ['Interested', 'Just browsing', 'Has questions'],
        asset: null,
        next: 'Product information, Lightweight response or Question handling',
        tonePreset: 'Friendly Guide',
        tone: FRIENDLY_GUIDE,
        test: {
            visitor: 'Tell me more.',
            bucky: 'Happy to. Here is what stands out today.',
            outcome: 'interested'
        }
    },
    {
        id: 'product-info',
        label: 'Product information',
        kind: 'conversation',
        x: 490,
        y: 85,
        purpose: 'Present the most relevant product using approved facts only.',
        behaviour: 'Lead with the main benefit, then one supporting detail.',
        responses: ['Wants to see it', 'Asks a question', 'Not now'],
        asset: null,
        next: 'Show relevant asset',
        tonePreset: 'Professional Consultant',
        tone: PROFESSIONAL,
        test: {
            visitor: 'What makes it different?',
            bucky: 'It is designed for everyday use. Would you like to see it?',
            outcome: 'wants to see it'
        }
    },
    {
        id: 'show-asset',
        label: 'Show relevant asset',
        kind: 'action',
        x: 635,
        y: 85,
        purpose: 'Display the matching visual asset card on screen.',
        behaviour: 'Keep the asset open while Bucky speaks, and let the visitor close it.',
        responses: ['Views asset', 'Closes asset'],
        asset: 'asset-01',
        next: 'Next action',
        tonePreset: 'Energetic Promoter',
        tone: ENERGETIC,
        test: {
            visitor: 'Yes, show me.',
            bucky: 'Here it is. Take your time.',
            outcome: 'asset displayed'
        }
    },
    {
        id: 'next-action',
        label: 'Next action',
        kind: 'action',
        x: 925,
        y: 175,
        purpose: 'Guide the visitor to a meaningful next step.',
        behaviour: 'Offer one clear action that fits the business, such as visiting the counter.',
        responses: ['Accepts', 'Declines politely'],
        asset: null,
        next: 'End session',
        tonePreset: 'Friendly Guide',
        tone: FRIENDLY_GUIDE,
        test: {
            visitor: 'Where do I go?',
            bucky: 'Head to the counter on your left. The team can help you there.',
            outcome: 'accepted'
        }
    },
    {
        id: 'light-response',
        label: 'Lightweight response',
        kind: 'conversation',
        x: 490,
        y: 260,
        purpose: 'Respect visitors who are just browsing.',
        behaviour: 'Acknowledge briefly and step back. No repeated pitching.',
        responses: ['Keeps playing', 'Leaves'],
        asset: null,
        next: 'Optional continuation',
        tonePreset: 'Calm',
        tone: CALM,
        test: {
            visitor: 'Just looking, thanks.',
            bucky: 'No problem, enjoy the game. Tap me if you need anything.',
            outcome: 'just browsing'
        }
    },
    {
        id: 'optional-continue',
        label: 'Optional continuation',
        kind: 'decision',
        x: 635,
        y: 260,
        purpose: 'Leave the door open without pressure.',
        behaviour: 'Only continue if the visitor reaches out again.',
        responses: ['Re-engages', 'Finishes'],
        asset: null,
        next: 'End session',
        tonePreset: 'Calm',
        tone: CALM,
        test: {
            visitor: '(Finishes the game)',
            bucky: 'Thanks for playing!',
            outcome: 'finished'
        }
    },
    {
        id: 'end',
        label: 'End session',
        kind: 'end',
        x: 780,
        y: 260,
        purpose: 'Close the interaction cleanly.',
        behaviour: 'Thank the visitor and reset for the next person.',
        responses: ['Session closed'],
        asset: null,
        next: 'Ready for the next visitor',
        tonePreset: 'Friendly Guide',
        tone: FRIENDLY_GUIDE,
        test: {
            visitor: 'Bye!',
            bucky: 'Thanks for stopping by. Have a great day!',
            outcome: 'session ended'
        }
    },
    {
        id: 'question-handling',
        label: 'Question handling',
        kind: 'conversation',
        x: 490,
        y: 435,
        purpose: 'Answer questions from business-approved information.',
        behaviour: 'If the answer is not confirmed, say so and offer to involve staff.',
        responses: ['Satisfied', 'Raises a concern'],
        asset: null,
        next: 'Objection handling',
        tonePreset: 'Professional Consultant',
        tone: PROFESSIONAL,
        test: {
            visitor: 'Does it come with a warranty?',
            bucky: 'I can share the warranty details the business has approved.',
            outcome: 'raises a concern'
        }
    },
    {
        id: 'objection',
        label: 'Objection handling',
        kind: 'conversation',
        x: 635,
        y: 435,
        purpose: 'Address a concern honestly and respectfully.',
        behaviour: 'Acknowledge the concern, answer truthfully, and respect a no.',
        responses: ['Reassured', 'Not interested'],
        asset: null,
        next: 'Relevant product information',
        tonePreset: 'Calm',
        tone: CALM,
        test: {
            visitor: 'It might be too expensive.',
            bucky: 'Fair question. I can show you the options and you can decide.',
            outcome: 'reassured'
        }
    },
    {
        id: 'relevant-info',
        label: 'Relevant product information',
        kind: 'conversation',
        x: 780,
        y: 435,
        purpose: 'Share the information that answers the concern.',
        behaviour: 'Keep to verified details and invite the next step.',
        responses: ['Wants the next step'],
        asset: null,
        next: 'Next action',
        tonePreset: 'Professional Consultant',
        tone: PROFESSIONAL,
        test: {
            visitor: 'What are the options?',
            bucky: 'Here are the approved options. The team can walk you through them.',
            outcome: 'wants the next step'
        }
    }
]

export const strategyEdges: StrategyEdge[] = [
    { from: 'start', to: 'opening' },
    { from: 'opening', to: 'checkpoint' },
    { from: 'checkpoint', to: 'product-info', label: 'Interested' },
    { from: 'checkpoint', to: 'light-response', label: 'Just browsing' },
    { from: 'checkpoint', to: 'question-handling', label: 'Has questions' },
    { from: 'product-info', to: 'show-asset' },
    { from: 'show-asset', to: 'next-action' },
    { from: 'light-response', to: 'optional-continue' },
    { from: 'optional-continue', to: 'end' },
    { from: 'question-handling', to: 'objection' },
    { from: 'objection', to: 'relevant-info' },
    { from: 'relevant-info', to: 'next-action' }
]

export const strategyPaths = [
    { id: 'interested', label: 'Interested', nodes: ['start', 'opening', 'checkpoint', 'product-info', 'show-asset', 'next-action'] },
    { id: 'browsing', label: 'Just browsing', nodes: ['start', 'opening', 'checkpoint', 'light-response', 'optional-continue', 'end'] },
    { id: 'questions', label: 'Has questions', nodes: ['start', 'opening', 'checkpoint', 'question-handling', 'objection', 'relevant-info', 'next-action'] }
] as const

export const guidedSteps = [
    { title: 'Opening pitch', nodeId: 'opening' },
    { title: 'Customer interest', nodeId: 'checkpoint' },
    { title: 'Product information', nodeId: 'product-info' },
    { title: 'Objection handling', nodeId: 'objection' },
    { title: 'Asset display', nodeId: 'show-asset' },
    { title: 'Ending action', nodeId: 'next-action' }
] as const

export const strategyAssets: StrategyAsset[] = [
    { id: 'asset-01', number: '01', title: 'Product photograph', type: 'Image', icon: 'image' },
    { id: 'asset-02', number: '02', title: 'Product information', type: 'Fact card', icon: 'info' },
    { id: 'asset-03', number: '03', title: 'PDF brochure', type: 'PDF', icon: 'pdf' },
    { id: 'asset-04', number: '04', title: 'Promotional material', type: 'Image', icon: 'promo' },
    { id: 'asset-05', number: '05', title: 'Event information', type: 'Fact card', icon: 'event' },
    { id: 'asset-06', number: '06', title: 'Service information', type: 'PDF', icon: 'service' }
]
